import type { PropsWithChildren } from "react";
import { useAppSelector } from "../../hooks/redux";
import { hasProjectNeoAccess } from "../../util/subdomain";
import { routes } from "./routes";

/** The same lazy element the ``*`` catch-all route mounts, so a denied
 *  beta route behaves exactly as if the route didn't exist. */
const RedirectElement = routes.redirectElementStar.element;

export type BetaGatedFeature = "neo" | "telehealth";

/**
 * Render-time gate for beta-only routes (Neo, Telehealth).
 *
 * These gates used to be applied by dropping the route entries out of the
 * router tree in ``mobileRouter``, which required rebuilding the router
 * whenever the entitlements resolved — and swapping the router instance
 * remounts every mounted page mid-boot (visible as a load → blank → reload
 * flash on fetch-heavy pages like Neo's Overview). Reading the entitlements
 * from redux at render time instead keeps a single router instance for the
 * whole session while preserving the deny behavior: an unauthorized visit
 * renders the same redirect element as the ``*`` catch-all, so no page
 * chunk is fetched and there's no evidence the feature exists.
 */
export const BetaRouteGuard = ({
  feature,
  children,
}: PropsWithChildren<{ feature: BetaGatedFeature }>) => {
  const { userEmail, betaFeaturesAllowedList } = useAppSelector((state) => ({
    userEmail: state.auth.user?.email,
    betaFeaturesAllowedList: state.auth.betaFeaturesAllowedList,
  }));

  const allowed =
    feature === "neo"
      ? hasProjectNeoAccess({ email: userEmail, betaFeaturesAllowedList })
      : Array.isArray(betaFeaturesAllowedList) &&
        betaFeaturesAllowedList.includes("telehealth");

  if (!allowed) return <RedirectElement />;
  return <>{children}</>;
};
